export interface QuizJoinMethods {
  cas: boolean;
  named: boolean;
  anonymous: boolean;
}

export type QuizJoinMethod = keyof QuizJoinMethods;

export interface MultipleChoiceScoring {
  mode: 'exact' | 'partial';
  /** Number of correct selections deducted for each incorrect selection. */
  wrongAnswerPenalty: number;
}

export interface QuizSettings {
  joinMethods?: Partial<QuizJoinMethods>;
  multipleChoiceScoring?: MultipleChoiceScoring;
  [key: string]: unknown;
}

export const DEFAULT_QUIZ_JOIN_METHODS: QuizJoinMethods = {
  cas: true,
  named: true,
  anonymous: false
};

export const DEFAULT_MULTIPLE_CHOICE_SCORING: MultipleChoiceScoring = {
  mode: 'exact',
  wrongAnswerPenalty: 1
};

export function normalizeMultipleChoiceScoring(value: unknown): MultipleChoiceScoring {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { ...DEFAULT_MULTIPLE_CHOICE_SCORING };
  }
  const scoring = value as Record<string, unknown>;
  return {
    mode: scoring.mode === 'partial' ? 'partial' : 'exact',
    wrongAnswerPenalty: typeof scoring.wrongAnswerPenalty === 'number' &&
      Number.isFinite(scoring.wrongAnswerPenalty) &&
      scoring.wrongAnswerPenalty >= 0 && scoring.wrongAnswerPenalty <= 10
      ? scoring.wrongAnswerPenalty
      : DEFAULT_MULTIPLE_CHOICE_SCORING.wrongAnswerPenalty
  };
}

export function normalizeQuizJoinMethods(joinMethods?: Partial<QuizJoinMethods> | null): QuizJoinMethods {
  const normalized: QuizJoinMethods = {
    cas: !!joinMethods?.cas,
    named: !!joinMethods?.named,
    anonymous: !!joinMethods?.anonymous
  };

  if (!normalized.cas && !normalized.named && !normalized.anonymous) {
    return { ...DEFAULT_QUIZ_JOIN_METHODS };
  }

  return normalized;
}

export function parseQuizSettings(settingsRaw: string | null | undefined): QuizSettings {
  if (!settingsRaw) {
    return {};
  }

  try {
    const parsed = JSON.parse(settingsRaw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as QuizSettings;
    }
  } catch (_err) {
    // Ignore invalid JSON and fall back to defaults.
  }

  return {};
}

export function getQuizJoinMethods(settingsRaw: string | null | undefined): QuizJoinMethods {
  const settings = parseQuizSettings(settingsRaw);
  return normalizeQuizJoinMethods(settings.joinMethods || null);
}

export function getMultipleChoiceScoring(settingsRaw: string | null | undefined): MultipleChoiceScoring {
  return normalizeMultipleChoiceScoring(parseQuizSettings(settingsRaw).multipleChoiceScoring);
}

export function updateQuizSettingsScoring(
  settingsRaw: string | null | undefined,
  scoring: MultipleChoiceScoring
): string {
  return JSON.stringify({
    ...parseQuizSettings(settingsRaw),
    multipleChoiceScoring: normalizeMultipleChoiceScoring(scoring)
  });
}

export function updateQuizSettingsJoinMethods(
  settingsRaw: string | null | undefined,
  joinMethods: Partial<QuizJoinMethods>
): string {
  const settings = parseQuizSettings(settingsRaw);
  const nextSettings: QuizSettings = {
    ...settings,
    joinMethods: normalizeQuizJoinMethods(joinMethods)
  };
  return JSON.stringify(nextSettings);
}

export function enabledJoinMethodsCount(joinMethods: QuizJoinMethods): number {
  return [joinMethods.cas, joinMethods.named, joinMethods.anonymous].filter(Boolean).length;
}

export function getJoinMethodFromAuthType(authType: string): QuizJoinMethod | null {
  if (authType === 'cas') return 'cas';
  if (authType === 'external') return 'named';
  if (authType === 'anonymous') return 'anonymous';
  return null;
}
